import { useEffect, useMemo, useReducer } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@skillarea/lib-frontend';
import {
  DailyRewardOpenResultDto,
  DailyRewardStateDto,
  EDailyRewardSlotStatus,
  EDailyRewardType,
} from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';

import {
  createChestPreviewOpenResult,
  simulateChestPreviewOpenDelay,
  type TChestPreviewReward,
} from './chestPreview';
import {
  CHEST_JUST_OPENED_DURATION_MS,
  CHEST_OPENING_DURATION_MS,
  normalizeSingle,
} from './chestUtils';

import { pushCustomEvent } from '~/lib/analytics/gtm';
import { playChestOpenSound } from '~/lib/game-sounds';
import { DailyRewardService } from '~/services/dailyRewardService';

export interface IChestOpeningState {
  pendingOpenDayIndex: number | null;
  openingDayIndex: number | null;
  justOpenedDayIndex: number | null;
  openingReward: DailyRewardOpenResultDto | null;
}

const initialChestOpeningState: IChestOpeningState = {
  pendingOpenDayIndex: null,
  openingDayIndex: null,
  justOpenedDayIndex: null,
  openingReward: null,
};

type TChestOpeningAction =
  | { type: 'START_OPEN'; dayIndex: number }
  | { type: 'OPEN_SUCCESS'; result: DailyRewardOpenResultDto }
  | { type: 'OPEN_ERROR' }
  | { type: 'COMPLETE_OPENING_ANIMATION' }
  | { type: 'COMPLETE_JUST_OPENED' }
  | { type: 'CLEAR_REWARD_OVERRIDE' }
  | { type: 'RESET' };

function chestOpeningReducer(
  state: IChestOpeningState,
  action: TChestOpeningAction,
): IChestOpeningState {
  switch (action.type) {
    case 'START_OPEN':
      return { ...state, pendingOpenDayIndex: action.dayIndex };
    case 'OPEN_SUCCESS':
      return {
        ...state,
        pendingOpenDayIndex: null,
        openingDayIndex: action.result.dayIndex,
        openingReward: action.result,
      };
    case 'OPEN_ERROR':
      return initialChestOpeningState;
    case 'COMPLETE_OPENING_ANIMATION':
      return {
        ...state,
        justOpenedDayIndex: state.openingDayIndex,
        openingDayIndex: null,
      };
    case 'COMPLETE_JUST_OPENED':
      return { ...state, justOpenedDayIndex: null };
    case 'CLEAR_REWARD_OVERRIDE':
      return { ...state, openingReward: null };
    case 'RESET':
      return initialChestOpeningState;
    default:
      return state;
  }
}

interface IUseChestOpeningOptions {
  rewardState?: DailyRewardStateDto;
  isPreviewMode?: boolean;
  previewReward?: TChestPreviewReward;
  onPreviewOpenSuccess?: (result: DailyRewardOpenResultDto) => void;
}

export function useChestOpening({
  isPreviewMode = false,
  onPreviewOpenSuccess,
  previewReward = 'coins',
  rewardState,
}: IUseChestOpeningOptions) {
  const { t } = useTranslation('dashboard');
  const queryClient = useQueryClient();
  const dailyRewardService = useMemo(() => new DailyRewardService(), []);
  const [openingState, dispatch] = useReducer(
    chestOpeningReducer,
    initialChestOpeningState,
  );

  const openMutation = useMutation({
    mutationFn: async (dayIndex: number) => {
      if (isPreviewMode) {
        await simulateChestPreviewOpenDelay();
        return createChestPreviewOpenResult(dayIndex, previewReward);
      }

      return normalizeSingle(await dailyRewardService.open());
    },
    onSuccess: (result, dayIndex) => {
      if (!result) return;

      if (result.rewardType === EDailyRewardType.coins) {
        playChestOpenSound('coins');
      } else if (result.rewardType === EDailyRewardType.achievement) {
        playChestOpenSound('achievement');
      } else {
        playChestOpenSound('empty');
      }

      dispatch({ type: 'OPEN_SUCCESS', result: { ...result, dayIndex } });
      if (isPreviewMode) {
        onPreviewOpenSuccess?.({ ...result, dayIndex });
      } else {
        queryClient.invalidateQueries({ queryKey: ['daily-rewards', 'state'] });
      }
      pushCustomEvent('chest_opened', { day: result.dayIndex });
    },
    onError: () => {
      dispatch({ type: 'OPEN_ERROR' });
      toast?.error(t('chests.openError'));
    },
  });

  useEffect(() => {
    if (!openingState.openingDayIndex) return;

    const timer = window.setTimeout(() => {
      dispatch({ type: 'COMPLETE_OPENING_ANIMATION' });
    }, CHEST_OPENING_DURATION_MS);

    return () => window.clearTimeout(timer);
  }, [openingState.openingDayIndex]);

  useEffect(() => {
    if (!openingState.justOpenedDayIndex) return;

    const timer = window.setTimeout(() => {
      dispatch({ type: 'COMPLETE_JUST_OPENED' });
    }, CHEST_JUST_OPENED_DURATION_MS);

    return () => window.clearTimeout(timer);
  }, [openingState.justOpenedDayIndex]);

  useEffect(() => {
    const { justOpenedDayIndex, openingDayIndex, openingReward } = openingState;
    if (!openingReward || !rewardState) return;
    if (openingDayIndex === openingReward.dayIndex) return;
    if (justOpenedDayIndex === openingReward.dayIndex) return;

    const isNowOpened = rewardState.slots.some(
      (slot) =>
        slot.dayIndex === openingReward.dayIndex &&
        slot.status === EDailyRewardSlotStatus.opened,
    );
    if (isNowOpened) dispatch({ type: 'CLEAR_REWARD_OVERRIDE' });
  }, [rewardState, openingState]);

  const startOpen = (dayIndex: number) => {
    dispatch({ type: 'START_OPEN', dayIndex });
    openMutation.mutate(dayIndex);
  };

  const resetOpening = () => {
    dispatch({ type: 'RESET' });
  };

  const isSlotTransitionActive = (dayIndex: number): boolean => {
    const {
      justOpenedDayIndex,
      openingDayIndex,
      openingReward,
      pendingOpenDayIndex,
    } = openingState;

    return (
      pendingOpenDayIndex === dayIndex ||
      openingDayIndex === dayIndex ||
      justOpenedDayIndex === dayIndex ||
      openingReward?.dayIndex === dayIndex
    );
  };

  return {
    openingState,
    isMutationPending: openMutation.isPending,
    isSlotTransitionActive,
    resetOpening,
    startOpen,
  };
}
